import { Link } from "@tanstack/react-router";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import TextMariaSection from "@/components/TextMariaSection";
import type { FaqItem } from "@/components/SeoLandingPage";
import { BLOG_AUTHOR, formatPostDate, type BlogPostMeta } from "@/lib/blog-posts";
import { buildFaqSchema } from "@/components/SeoLandingPage";
import { buildBreadcrumbSchema, ldScript, pageHead, SITE_URL, BUSINESS_ID } from "@/lib/page-schema";

export function blogPostHead(post: BlogPostMeta, faqs: FaqItem[]) {
  const path = `/blog/${post.slug}`;
  const url = `${SITE_URL}${path}`;
  const h = pageHead({
    path,
    title: `${post.title} | Atlanta Luxury Sprinters`,
    description: post.description,
    ogType: "article",
    scripts: [
      ldScript({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.description,
        datePublished: post.date,
        dateModified: post.date,
        mainEntityOfPage: url,
        url,
        author: { "@type": "Person", name: BLOG_AUTHOR },
        publisher: { "@type": "Organization", "@id": BUSINESS_ID, name: "Atlanta Luxury Sprinters", url: SITE_URL },
      }),
      ldScript(buildFaqSchema(faqs)),
      ldScript(
        buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path },
        ]),
      ),
    ],
  });
  h.meta.push({ property: "article:published_time", content: post.date });
  return h;
}

export default function BlogPostLayout({
  post,
  faqs,
  children,
}: {
  post: BlogPostMeta;
  faqs: FaqItem[];
  children: React.ReactNode;
}) {
  return (
    <>
      <Header />
      <main>
        <article className="pt-32 pb-16 bg-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="text-sm text-primary underline underline-offset-4">Blog</Link>
            <h1 className="font-heading text-3xl sm:text-5xl font-semibold text-foreground leading-tight mt-6 mb-4">{post.title}</h1>
            <p className="text-sm text-muted-foreground mb-10">
              By {BLOG_AUTHOR} · <time dateTime={post.date}>{formatPostDate(post.date)}</time>
            </p>
            <div className="blog-prose space-y-5 text-lg leading-relaxed text-muted-foreground [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h2]:pt-6 [&_h3]:font-heading [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:pt-2 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2">
              {children}
            </div>

            <section className="mt-16">
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-6">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((f) => (
                  <div key={f.q} className="luxury-card rounded-lg p-6">
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{f.q}</h3>
                    <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </article>
        <TextMariaSection />
      </main>
      <Footer />
      <FloatingButtons />
    </>
  );
}
